import { parsePrice } from "@bcc/lib/utils";
import type { WebStatusUpdate } from "@shared/services/salesforce/types";
import type { ShipToInfo } from "@shared/types";
import type { ResolvedStatus } from "./resolve-web-status";
import { toWebStatusUpdate } from "./web-status-update";
/**
 * A price resolver's single-valued fallback. Prices arrive from the product
 * fetch as either a number or a numeric string, and are absent on rows the
 * fetch hasn't populated — so the resolvers take and return the same shape.
 */
export type Fallback = string | number | undefined;

/**
 * Sales channel → ISO destination country. Mirrors COUNTRY_TO_SALES_CHANNEL in
 * shared, which the frontend cannot import (it pulls in runtime
 * infrastructure). Kept as an explicit map rather than a "strip the B prefix"
 * parse because the codes are NOT a naive prefix: the UK sales channel is
 * "BUKMP" while the roaster's ISO country code is "GB". DE/NL/US/AU happen to
 * coincide; GB is the one that does not.
 *
 * Only the Beanz marketplace channels ("*MP") are mapped. The product fetch
 * also emits non-marketplace channels for the same country ("BUK", "PUK",
 * "ZCGUK", "APPUK" alongside "BUKMP"), which carry other storefronts' values.
 * Those are deliberately unmapped: the Products page shows the Beanz
 * marketplace view, and the marketplace channel is always present when the SKU
 * ships to a country.
 */
const CHANNEL_TO_COUNTRY: Record<string, string> = {
  BUSMP: "US",
  BUKMP: "GB",
  BAUMP: "AU",
  BDEMP: "DE",
  BNLMP: "NL",
  PBBUS: "US", // Used instead of BUSMP when the vendor's sellingPlatforms includes PBB
};

/**
 * Canonicalises an ISO country code for ship-to comparisons. The country →
 * channel map is many-to-one — both "GB" and "UK" address the "BUKMP" channel —
 * so a vendor stored as either must match the same channel entry.
 */
const normaliseCountryCode = (countryCode: string | null | undefined): string | undefined => {
  if (!countryCode) return undefined;
  const trimmed = countryCode.trim().toUpperCase();
  if (!trimmed) return undefined;
  return trimmed === "UK" ? "GB" : trimmed;
};

/**
 * Parses a sales-channel code into its canonical ISO destination country code:
 * "BDEMP" → "DE", "BNLMP" → "NL", "BUKMP" → "GB". Returns undefined for
 * channels this app doesn't render (see CHANNEL_TO_COUNTRY).
 */
export const parseChannelCountryCode = (channel: string | undefined): string | undefined => {
  if (!channel) return undefined;
  return CHANNEL_TO_COUNTRY[channel.trim().toUpperCase()];
};

/**
 * A ship-to country's current (existing) values, used as the `current*`
 * comparison fields the backend diffs against the requested new values when
 * building per-country approval-request history.
 */
export interface ShipToCurrentValues {
  countryCode: string;
  currentMsrp?: number;
  currentWholesalePrice?: number;
  currentWebStatus?: WebStatusUpdate;
  currentWeeklyInventory?: number;
  currentTotalInventory?: number;
}

/**
 * Fallback single-valued current data used for any ship-to country the product
 * fetch hasn't returned a per-channel `shipToInfo` entry for (non-cross-border
 * SKUs, or pre-rollout data). Sourced from the variant's single-valued fields.
 */
export interface ShipToFallback {
  msrp?: number;
  wholesalePrice?: number;
  webStatus?: WebStatusUpdate;
  weeklyInventory?: number;
  totalInventory?: number;
}

/**
 * Resolves the per-ship-to-country current values for an approval request, one
 * entry per destination country. `countries` is the authoritative ship-to list
 * (the SKU's destinations); when empty the request covers only the roaster's own
 * country. Each country's current values come from its matching `shipToInfo`
 * channel entry, falling back to the variant's single-valued fields when no
 * per-channel entry is present.
 */
export const buildShipToCurrentValues = (
  countries: string[],
  shipToInfo: ShipToInfo[] | undefined,
  roasterCountryCode: string,
  fallback: ShipToFallback
): ShipToCurrentValues[] => {
  const infoByCountry = new Map<string, ShipToInfo>();
  for (const info of shipToInfo ?? []) {
    const countryCode = parseChannelCountryCode(info.channel);
    // First entry wins in case the fetch ever repeats a channel for a country.
    // Matches findShipToInfoForCountry, which takes the first match too.
    if (countryCode && !infoByCountry.has(countryCode)) infoByCountry.set(countryCode, info);
  }
  const targetCountries = countries.length > 0 ? countries : [roasterCountryCode];
  return targetCountries.map((countryCode) => {
    // Look up canonically ("UK" → "GB") but emit the caller's own code: it is
    // the ship-to country the backend keys the approval-request history on.
    const info = infoByCountry.get(normaliseCountryCode(countryCode) ?? "");
    return {
      countryCode,
      currentMsrp: info?.price !== undefined ? parsePrice(info.price) : fallback.msrp,
      currentWholesalePrice: info?.wholesalePrice !== undefined ? parsePrice(info.wholesalePrice) : fallback.wholesalePrice,
      currentWebStatus: toWebStatusUpdate(info?.webStatus) ?? fallback.webStatus,
      // The product fetch reports a channel's stock as `inventoryQuantity`;
      // `weeklyInventory` is only ever set on the variant's single-valued
      // fields. Read both so a country with a per-channel entry is never
      // resolved from the caller's fallback, which on the price path is the
      // Salesforce-overlaid figure rather than the approved one.
      currentWeeklyInventory: info?.weeklyInventory ?? info?.inventoryQuantity ?? fallback.weeklyInventory,
      currentTotalInventory: info?.totalInventory ?? fallback.totalInventory,
    };
  });
};

/**
 * Finds the per-channel `shipToInfo` entry whose sales channel maps to the given
 * destination country code (e.g. "DE" → the "BDEMP" channel). Returns
 * undefined for non-cross-border SKUs (no `shipToInfo`) or when no channel
 * matches the country — callers then fall back to the variant's single-valued
 * field.
 */
export const findShipToInfoForCountry = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined
): ShipToInfo | undefined => {
  const target = normaliseCountryCode(countryCode);
  if (!shipToInfo?.length || !target) return undefined;
  return shipToInfo.find((info) => parseChannelCountryCode(info.channel) === target);
};

/**
 * Resolves the inventory figure to display for the logged-in country. Prefers the
 * current country's per-channel `shipToInfo.inventoryQuantity` (which reflects an
 * approved Inventory Update), falling back to the variant's single-valued figure
 * for non-cross-border SKUs / pre-rollout data.
 */
export const resolveShipToInventory = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined,
  fallback: number
): number => findShipToInfoForCountry(shipToInfo, countryCode)?.inventoryQuantity ?? fallback;

/**
 * Resolves the active subscriber count to display for the logged-in country.
 * Prefers the current country's per-channel `shipToInfo.activeSubscriberCount`
 * (cross-border SKUs), falling back to the variant's single-valued figure for
 * non-cross-border SKUs / pre-rollout data. The multi-channel PIM fetch leaves
 * the variant's top-level `activeSubscriberCount` empty and carries the
 * per-country count in `shipToInfo` instead.
 */
export const resolveShipToSubscribers = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined,
  fallback: number
): number => findShipToInfoForCountry(shipToInfo, countryCode)?.activeSubscriberCount ?? fallback;

/**
 * Resolves the web status to display for the logged-in country. Prefers the
 * current country's per-channel `shipToInfo.webStatus` (which reflects an approved
 * Status Update), falling back to the row's resolved status for non-cross-border
 * SKUs / pre-rollout data.
 */
export const resolveShipToWebStatus = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined,
  fallback: ResolvedStatus | undefined
): ResolvedStatus | undefined => findShipToInfoForCountry(shipToInfo, countryCode)?.webStatus ?? fallback;

/**
 * Resolves the retail price (MSRP/RRP) to display for the logged-in country.
 * Prefers the current country's per-channel `shipToInfo.price` — on PIM-first
 * rows that is the only place an approved Price Update lands, because a PIM
 * variant carries no top-level price and the variant's flat `price` is filled
 * in from the (staler) Salesforce overlay. Falls back to that flat value for
 * non-cross-border SKUs / pre-rollout data.
 */
export const resolveShipToPrice = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined,
  fallback: Fallback
): Fallback => findShipToInfoForCountry(shipToInfo, countryCode)?.price ?? fallback;

/**
 * Resolves the wholesale price to display for the logged-in country. Same
 * PIM-over-Salesforce precedence as `resolveShipToPrice`.
 */
export const resolveShipToWholesalePrice = (
  shipToInfo: ShipToInfo[] | undefined,
  countryCode: string | null | undefined,
  fallback: Fallback
): Fallback => findShipToInfoForCountry(shipToInfo, countryCode)?.wholesalePrice ?? fallback;
